<template>
  <u-popover v-model:visible="visible" placement="bottom-start" trigger="click">
    <template #content>
      <div class="select-search" :style="{ width: popoverWidth }">
        <u-input
          v-model="searchKeyword"
          clearable
          :placeholder="
            $t('components.ModelSelect.a9e989180900ee37d55c103d9599bf8e')
          "
        />
      </div>
      <ul
        v-if="filteredOptions.length"
        class="select-list"
        :style="{ width: popoverWidth }"
      >
        <li v-for="item in filteredOptions" :key="item.value" class="mb-[16px]">
          <div class="select-item-label">
            {{ item.label }}
          </div>
          <u-tooltip
            v-for="child in item.children"
            :key="child.value"
            :title="showOptionTooltip ? child.label : ''"
            :disabled="!showOptionTooltip"
            placement="right"
          >
            <div
              class="select-item-secondary-label cursor-pointer"
              :class="{
                active: child.value === modelValue
              }"
              :data-testid="`model-option-${child.value}`"
              @click="handleClick(child)"
            >
              <img
                v-if="normalizeIcon(child.icon)"
                class="select-icon"
                :src="normalizeIcon(child.icon)"
              />
              <div class="flex min-w-0 flex-1 items-center">
                <div class="flex min-w-0 items-center">
                  <span class="min-w-0 ellipsis">{{ child.label }}</span>
                  <span
                    v-if="
                      showDeepThinkingLabel
                        ? child.support_deep_thinking
                        : child.support_thinking
                    "
                    class="ml-[4px] inline-flex h-[20px] shrink-0 items-center rounded-[4px] bg-[var(--background-primary-bg-color-primary)] px-[4px] text-xs leading-[20px] text-[var(--status-primary-color-primary)]"
                    >{{
                      $t(
                        'components.ModelSelect.127c433977896935942eab18235c54f1'
                      )
                    }}</span
                  >
                </div>

                <span
                  v-if="child.support_function_call"
                  class="model-feature-tag ml-[4px] inline-flex h-[20px] shrink-0 items-center rounded-[4px] px-[4px] text-xs leading-[20px] text-[var(--status-success-color-success)]"
                  >{{
                    $t(
                      'components.ModelSelect.6b502a6b92f53aad1ba43dd29d57d779'
                    )
                  }}</span
                >
                <span
                  v-if="child.support_multimodal"
                  class="model-feature-tag shrink-0"
                  >{{
                    $t(
                      'components.ModelSelect.ba9b34f54e99a94d8a8243e4a17a9e23'
                    )
                  }}</span
                >
              </div>
              <u-tooltip
                :title="child.model_feature"
                placement="right"
                :disabled="showOptionTooltip"
              >
                <BotIcon
                  name="question-v2"
                  padding="0"
                  size="16"
                  class="mt-[1px] pl-[4px] cursor-pointer text-text-neutral-tertiary"
                />
              </u-tooltip>
            </div>
          </u-tooltip>
        </li>
      </ul>
      <div v-else class="select-empty" :style="{ width: popoverWidth }">
        {{ $t('components.ModelSelect.105f20a0ad08a0ae19e1fe41db25fb29') }}
      </div>
    </template>
    <div ref="selectRef" class="select cursor-pointer" :class="{ disabled }">
      <div class="flex min-w-0 flex-1 items-center">
        <img
          v-if="current.label && normalizeIcon(current.icon)"
          class="select-icon"
          :src="normalizeIcon(current.icon)"
        />
        <span
          class="min-w-0 ellipsis"
          :class="{ placeholder: !current.label }"
          >{{ current.label }}</span
        >
        <span
          v-if="
            showDeepThinkingLabel
              ? current.support_deep_thinking
              : current.support_thinking
          "
          class="ml-[4px] inline-flex h-[20px] shrink-0 items-center rounded-[4px] bg-[var(--background-primary-bg-color-primary)] px-[4px] text-xs leading-[20px] text-[var(--status-primary-color-primary)]"
          >{{
            $t('components.ModelSelect.127c433977896935942eab18235c54f1')
          }}</span
        >
        <span
          v-if="current.support_function_call"
          class="ml-[4px] inline-flex h-[20px] shrink-0 items-center rounded-[4px] bg-[var(--background-success-bg-color-success)] px-[4px] text-xs leading-[20px] text-[var(--status-success-color-success)]"
          >{{
            $t('components.ModelSelect.6b502a6b92f53aad1ba43dd29d57d779')
          }}</span
        >
        <span
          v-if="current.support_multimodal"
          class="model-feature-tag shrink-0"
          >{{
            $t('components.ModelSelect.ba9b34f54e99a94d8a8243e4a17a9e23')
          }}</span
        >
      </div>
      <BotIcon
        :padding="0"
        name="arrow-down-s-line"
        :rotate="visible ? 0 : 180"
        size="16"
        color="var(--font-color)"
      />
    </div>
  </u-popover>
</template>
<script>
import { defineComponent, ref, computed, watch, nextTick } from 'vue';

export default defineComponent({
  name: 'SelectGroup',
  props: {
    options: {
      type: Array,
      default: () => []
    },
    modelValue: {
      type: String,
      default: ''
    },
    width: {
      type: Number,
      default: 200
    },
    disabled: {
      type: Boolean,
      default: false
    },

    showDeepThinkingLabel: {
      type: Boolean,
      default: false
    },
    // When enabled the whole option row shows the full model name on hover,
    // which is useful for truncated labels. The small question icon falls back
    // to its own model feature tooltip when this is off.
    showOptionTooltip: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const visible = ref(false);
    const selectRef = ref(null);
    const popoverWidth = ref('auto');
    const searchKeyword = ref('');

    const filteredOptions = computed(() => {
      const keyword = searchKeyword.value.trim().toLowerCase();
      if (!keyword) return props.options;

      return props.options
        .map(item => {
          const groupMatched = item.label.toLowerCase().includes(keyword);
          const children = groupMatched
            ? item.children
            : item.children.filter(child =>
                child.label.toLowerCase().includes(keyword)
              );

          return {
            ...item,
            children
          };
        })
        .filter(item => item.children.length);
    });

    const current = computed(() => {
      const childArr = props.options.flatMap(item => item.children);
      return (
        childArr.find(child => child.value === props.modelValue) || {
          label: ''
        }
      );
    });

    const normalizeIcon = icon => {
      if (!icon || typeof icon !== 'string') return '';
      if (icon.startsWith('http') || icon.startsWith('//')) return icon;
      return `/${icon.replace(/^\/+/, '')}`;
    };

    const updatePopoverWidth = async () => {
      if (selectRef.value) {
        await nextTick();
        const width = selectRef.value.offsetWidth;
        if (width > 0) {
          popoverWidth.value = `${width - 15}px`;
        }
      }
    };

    watch(visible, async newVal => {
      if (newVal) {
        await updatePopoverWidth();
      } else {
        searchKeyword.value = '';
      }
    });

    const handleClick = child => {
      if (props.disabled) return;
      emit('update:modelValue', child.value);
      emit('change', child.value);
      visible.value = false;
    };

    return {
      visible,
      selectRef,
      popoverWidth,
      searchKeyword,
      filteredOptions,
      handleClick,
      current,
      normalizeIcon
    };
  }
});
</script>
<style lang="less" scoped>
.select {
  background-color: var(--form-background-color);
  border: 1px solid var(--border-neutral-color-secondary);
  width: 100%;
  height: 40px;
  border-radius: 8px;
  padding: 0 8px;
  color: var(--active-font-color);
  display: flex;
  align-items: center;
  &.disabled {
    opacity: 0.5;
  }
}
.select-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-right: 8px;
}
.select-list {
  padding: 8px;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
}
.select-search {
  padding: 8px 8px 0;
}
.select-empty {
  padding: 16px 8px;
  color: var(--font-color);
  text-align: center;
}
.select-item-label {
  height: 18px;
  line-height: 18px;
  color: var(--font-color);
  margin-bottom: 8px;
  padding: 0 8px;
}
.select-item-secondary-label {
  height: 40px;
  line-height: 40px;
  color: var(--active-font-color);
  padding: 0 8px;
  margin-bottom: 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    background-color: var(--form-hover-background-color);
  }
  &.active {
    background-color: var(--form-hover-background-color);
  }
}
.model-feature-tag {
  display: inline-block;
  font-size: 12px;
  line-height: 18px;
  padding: 0 4px;
  border: 1px solid var(--border-neutral-color-secondary);
  border-radius: 4px;
  margin-left: 4px;
  color: var(--text-neutral-color-text-tertiary);
}
</style>
